import { useState, useRef, useEffect, useContext } from "react";
import styled, { ThemeContext } from "styled-components";

import { color, ColorProps, LayoutProps, layout } from "styled-system";

import Button from "../Button/Button";
import DropdownOption from "../DropdownOptions/DropdownOption";
import DropdownOptions from "../DropdownOptions/DropdownOptions";
import Icon from "../Icon/Icon";

const StyledDiv = styled.div<ColorProps & LayoutProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  transform: skewX(-13deg);
  border-radius: 6px 0 0 12px;
  position: relative;
  text-overflow: ellipsis;
  padding: 0 12px 0px 20px;
  box-sizing: border-box;
  ${color}
  ${layout}
`;

const CaretContainer = styled.div`
  transform: skewX(13deg);
  display: flex;
`;

export const useKeyPress = function (targetKey: string) {
  const [keyPressed, setKeyPressed] = useState(false);
  function downHandler(event: KeyboardEvent) {
    event.stopPropagation();
    if (event.key === targetKey) {
      setKeyPressed(true);
    }
  }

  const upHandler = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === targetKey) {
      setKeyPressed(false);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);

    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
    };
  });

  return keyPressed;
};

export default function VehicleTypes({
  options,
  selectedType,
  onVehicleTypeChange,
}: {
  options: { shortName: string; withCount: string }[];
  selectedType: string;
  onVehicleTypeChange: (string: string) => void;
}) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const downPress = useKeyPress("ArrowDown");
  const upPress = useKeyPress("ArrowUp");
  const enterPress = useKeyPress("Enter");
  const [cursor, setCursor] = useState<number>(0);
  const [hovered, setHovered] = useState<string | undefined>(undefined);

  const theme = useContext(ThemeContext);
  const dropdownNode = useRef<HTMLDivElement>(null);
  const backgroundColor = expanded ? "primary-white" : "#313a51";

  const color = expanded ? "primary" : "primary-white";

  const toggle = () => {
    setExpanded(!expanded);
  };

  const handleOutsideClick = (e: MouseEvent) => {
    const targetNode = e.target as Element;
    if (dropdownNode.current && dropdownNode.current.contains(targetNode)) {
      return;
    }
    setExpanded(false);
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (options.length && downPress && expanded) {
      setCursor((prevState) =>
        prevState < options.length - 1 ? prevState + 1 : prevState
      );
    }
  }, [downPress, expanded, options]);

  useEffect(() => {
    if (options.length && upPress && expanded) {
      setCursor((prevState) => (prevState > 0 ? prevState - 1 : prevState));
    }
  }, [upPress, options, expanded]);

  useEffect(() => {
    if (options.length && enterPress) {
      onVehicleTypeChange(options[cursor].shortName);
    }
  }, [cursor, enterPress, options, onVehicleTypeChange]);

  useEffect(() => {
    if (options.length && hovered) {
      setCursor(options.findIndex((o) => o.withCount === hovered));
    }
  }, [hovered, options]);

  const Options = options.map((option, index) => (
    <DropdownOption
      name={option.withCount}
      key={option.shortName}
      onClick={() => {
        onVehicleTypeChange(option.shortName);
        setExpanded(false);
      }}
      setHovered={setHovered}
      active={index === cursor}
    ></DropdownOption>
  ));

  return (
    <div style={{ position: "relative" }} ref={dropdownNode}>
      <StyledDiv
        bg={backgroundColor}
        onClick={toggle}
        onKeyDown={(e: any) => (e.key === "Enter" ? toggle() : () => {})}
        width={80}
        height={{ _: "47px", desktop: "55px" }}
        tabIndex={0}
      >
        <Button
          aria-haspopup="true"
          aria-expanded={expanded}
          css={`
            color: ${theme.colors[color]};
            transform: skewX(13deg);
            text-overflow: ellipsis;
            overflow: hidden;
            display: block;
          `}
          variant="tertiary"
          text={selectedType != null ? selectedType : ""}
          width="auto"
          px={0}
          type="button"
        ></Button>
        <CaretContainer>
          <Icon
            name={expanded ? "angle-up" : "angle-down"}
            weight="regular"
            color={color}
            width="16px"
            height="17px"
            ml={1}
            pt="2px"
          />
        </CaretContainer>
      </StyledDiv>
      {expanded && Options.length > 0 && (
        <DropdownOptions
          position="absolute"
          top={[52, 63]}
          left={[-16, -7]}
          height={["calc(100vh - 112px)", "auto"]}
          close={() => {
            setExpanded(false);
          }}
        >
          {Options}
        </DropdownOptions>
      )}
    </div>
  );
}
